import './RtuCluster.css';

import React, { useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useTranslation } from 'react-i18next';
import TruncatedValue from '../common/TruncatedValue/TruncatedValue';
import { keepOverlayClear, scheduleOverlayClear } from '../common/TruncatedValue/overlayPosition';
import type { RtuFormerWell, RtuWell } from '../../types/RTU';
import {
  getWellView,
  hasServedByEvidence,
  matchingOtherWell,
  resolveServedBy,
  WellEntry,
} from './rtuCluster';

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

interface NameCellProps {
  name: string;
  columnLabel: string;
  /** 1 for a child rendered under a present master. */
  depth: 0 | 1;
  /** > 0 renders the expand/collapse chevron. */
  childCount: number;
  expanded: boolean;
  onToggle?: () => void;
  onOpenDetails?: () => void;
  /** Grouped view keeps its existing click-to-open name; the list view relies on its existing row navigation. */
  linkStyle?: boolean;
  /** Cluster layout is on: every row reserves the chevron width so root names align and children sit 16px further in. */
  slot?: boolean;
  /** Last child under its master: the tree connector ends here. */
  isLastChild?: boolean;
  /**
   * Small icon-only marker after the name: a standalone former well record, or an unresolved identity.
   * The marker is a fixed 16px box, so it never takes width from the name.
   */
  marker?: 'former' | 'unresolved';
}

/** Generic wording only: it never refers to other records, serials, masters, children or counts. */
export const UNRESOLVED_IDENTITY_TEXT = 'Identity unresolved - this record is not attributed to one device';
export const FORMER_RECORD_TEXT = 'Former well record';

/** Icon-only record marker (former record / unresolved identity), labelled for screen readers and by tooltip. */
export const RecordMarker: React.FC<{ kind: 'former' | 'unresolved' }> = ({ kind }) => {
  const { t } = useTranslation();
  const text =
    kind === 'former'
      ? t('rtuCluster.formerWellRecord', { defaultValue: FORMER_RECORD_TEXT })
      : t('rtuCluster.identityUnresolved', { defaultValue: UNRESOLVED_IDENTITY_TEXT });
  return (
    <span
      className={`rtu-cluster-marker rtu-cluster-marker--${kind}`}
      role="img"
      aria-label={text}
      title={text}
      tabIndex={0}
      onClick={stop}
      onMouseDown={stop}
      onKeyDown={stop}
    >
      <i className={`pi ${kind === 'former' ? 'pi-history' : 'pi-exclamation-circle'}`} aria-hidden="true" />
    </span>
  );
};

/** Name cell: optional cluster chevron, 16px indent + 1px guide for children, truncation with copy. */
export const ClusterNameCell: React.FC<NameCellProps> = ({
  name,
  columnLabel,
  depth,
  childCount,
  expanded,
  onToggle,
  onOpenDetails,
  linkStyle,
  slot,
  isLastChild,
  marker,
}) => {
  const { t } = useTranslation();
  const action = expanded
    ? t('rtuCluster.collapse', { defaultValue: 'Collapse' })
    : t('rtuCluster.expand', { defaultValue: 'Expand' });
  return (
    <div
      className={[
        'rtu-cluster-name',
        depth === 1 ? 'rtu-cluster-name--child' : '',
        depth === 1 && isLastChild ? 'rtu-cluster-name--last' : '',
        childCount > 0 && slot ? 'rtu-cluster-name--master' : '',
        childCount > 0 && slot && expanded ? 'rtu-cluster-name--open' : '',
        linkStyle ? 'rtu-cluster-name--link' : '',
      ].filter(Boolean).join(' ')}
    >
      {childCount > 0 && onToggle && (
        <button
          type="button"
          className="rtu-cluster-chevron"
          aria-expanded={expanded}
          aria-label={`${action} ${name} (${childCount} ${t('rtuCluster.childRtus', { defaultValue: 'child RTUs' })})`}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onToggle();
          }}
          onMouseDown={stop}
          onDoubleClick={stop}
          onTouchEnd={stop}
          onKeyDown={stop}
        >
          <i className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} aria-hidden="true" />
        </button>
      )}
      {slot && !(childCount > 0 && onToggle) && <span className="rtu-cluster-chevron-slot" aria-hidden="true" />}
      <TruncatedValue value={name} label={columnLabel} onOpenDetails={onOpenDetails} />
      {marker && <RecordMarker kind={marker} />}
    </div>
  );
};

type RtuWithWells = {
  id: number;
  name?: string | null;
  wells?: RtuWell[] | null;
  formerWells?: RtuFormerWell[] | null;
};

/**
 * The row always shows its own reference well (the declared primary, or the record well). A search never replaces it:
 * an additional well the query matches is only highlighted inside the "+N" wells popover.
 */

interface WellNameProps {
  rtu: RtuWithWells;
  columnLabel: string;
  query?: string | null;
  /** Resolves a present (visible) RTU row by id, to name the child RTU that serves a well. */
  resolveRtu: (id: number) => { id: number; name: string } | undefined;
}

/** Well name of the row (single line) plus a "+N" button that lists every well this RTU reports. */
export const WellNameCell: React.FC<WellNameProps> = ({ rtu, columnLabel, query, resolveRtu }) => {
  const { t } = useTranslation();
  const panelRef = useRef<OverlayPanel | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const { shown: well, extras, all } = getWellView(rtu);
  const match = matchingOtherWell(rtu, query);
  if (!well) {
    return null;
  }
  const rowName = rtu.name ?? String(rtu.id);
  const title = `${t('rtuCluster.wellsOf', { defaultValue: 'Wells of' })} ${rowName}`;
  const showServedBy = hasServedByEvidence(all) || all.some((item) => item.kind === 'record');
  // Owner rule (2026-10-08): no "primary/secondary well" wording in the UI. The list stays in the reported order and
  // "Served by" carries the meaning; the report-record well (when it differs from the declared one) is labelled there.
  const servedByText = (item: WellEntry) => {
    if (item.kind === 'record') {
      return t('rtuCluster.servedByThisRecord', { defaultValue: 'This RTU (report record)' });
    }
    const served = resolveServedBy(item, resolveRtu);
    switch (served.kind) {
      case 'this':
        return t('rtuCluster.servedByThis', { defaultValue: 'This RTU' });
      case 'child':
        return served.name;
      case 'childAnonymous':
        return t('rtuCluster.servedByChild', { defaultValue: 'Child RTU' });
      default:
        return t('rtuCluster.servedByUnknown', { defaultValue: 'Not reported' });
    }
  };
  const returnFocusToButton = () => buttonRef.current?.focus();
  const closePanel = () => {
    panelRef.current?.hide();
    returnFocusToButton();
  };
  const onDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closePanel();
      return;
    }
    if (event.key === 'Tab') {
      const dialog = dialogRef.current;
      const focusable = dialog
        ? Array.from(dialog.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        : [];
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
    event.stopPropagation();
  };

  return (
    <div className="rtu-cluster-well">
      <TruncatedValue value={well.name ?? ''} label={columnLabel} />
      {extras.length > 0 && (
        <button
          ref={buttonRef}
          type="button"
          className={`rtu-cluster-wells-button${match ? ' rtu-cluster-wells-button--match' : ''}`}
          aria-label={`${t('rtuCluster.showWells', { defaultValue: 'Show all wells of' })} ${rowName} (${all.length})`}
          aria-haspopup="dialog"
          aria-expanded={open}
          title={t('rtuCluster.showWellsTitle', { defaultValue: 'All wells of this RTU' })}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            const trigger = event.currentTarget;
            panelRef.current?.toggle(event);
            scheduleOverlayClear(() => panelRef.current?.getElement(), trigger);
          }}
          onMouseDown={stop}
          onDoubleClick={stop}
          onTouchEnd={stop}
          onKeyDown={stop}
        >
          +{extras.length}
        </button>
      )}
      <OverlayPanel
        ref={panelRef}
        dismissable
        closeOnEscape
        className="rtu-cluster-panel"
        onShow={() => {
          keepOverlayClear(panelRef.current?.getElement(), buttonRef.current);
          setOpen(true);
          dialogRef.current?.focus();
        }}
        onHide={() => {
          setOpen(false);
          // Focus was inside the closed panel (or lost with it): give it back to the button that opened it.
          const active = document.activeElement;
          if (!active || active === document.body) {
            returnFocusToButton();
          }
        }}
      >
        <div
          ref={dialogRef}
          className="rtu-cluster-panel__dialog"
          role="dialog"
          aria-label={title}
          tabIndex={-1}
          onClick={stop}
          onMouseDown={stop}
          onTouchEnd={stop}
          onKeyDown={onDialogKeyDown}
        >
          <div className="rtu-cluster-panel__head">
            <div className="rtu-cluster-panel__title">{title}</div>
            <button
              type="button"
              className="rtu-cluster-panel__close"
              aria-label={t('rtuCluster.closeWells', { defaultValue: 'Close wells list' })}
              onClick={closePanel}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          </div>
          <table className="rtu-cluster-panel__table">
            <caption className="dms-visually-hidden">{title}</caption>
            <thead>
              <tr>
                <th scope="col">{t('rtuCluster.wellName', { defaultValue: 'Well name' })}</th>
                <th scope="col">{t('rtuCluster.wellId', { defaultValue: 'Well ID' })}</th>
                {showServedBy && <th scope="col">{t('rtuCluster.servedBy', { defaultValue: 'Served by' })}</th>}
              </tr>
            </thead>
            <tbody>
              {all.map((item, index) => (
                <tr
                  key={`${item.kind}-${item.wellId}-${index}`}
                  className={match && item.wellId === match.wellId ? 'rtu-cluster-panel__row--match' : undefined}
                >
                  <td>{item.name ?? ''}</td>
                  <td>{item.wellId}</td>
                  {showServedBy && (
                    <td className={item.servedBy === 'CHILD_RTU' ? 'rtu-cluster-panel__child' : undefined}>{servedByText(item)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </OverlayPanel>
    </div>
  );
};

interface WellIdProps {
  rtu: RtuWithWells;
  columnLabel: string;
  query?: string | null;
}

/** Well ID of the row: the same reference well as the Well name column; a search never changes it. */
export const WellIdCell: React.FC<WellIdProps> = ({ rtu, columnLabel }) => {
  const { shown: well } = getWellView(rtu);
  if (!well) {
    return null;
  }
  return (
    <div className="rtu-cluster-well">
      <TruncatedValue value={well.wellId} label={columnLabel} />
    </div>
  );
};
